"use client";

import { useCallback, useState } from "react";

import type { Post } from "@/data/blog-posts";
import { generateId } from "@/lib/generate-id";

import { BlogContent } from "./blog-content";
import { PostMetadata } from "./post-metadata";
import PostShare from "./post-share";
import PostTags from "./post-tags";
import { RelatedPosts } from "./related-posts";
import { TableOfContents } from "./table-of-contents";

export interface Heading {
  id: string;
  text: string;
  level: number;
}

interface BlogPostProps {
  post: Post;
  relatedPosts: Post[];
}

export function BlogPost({ post, relatedPosts }: BlogPostProps) {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string>("");

  const handleHeadingsReady = useCallback((container: HTMLElement) => {
    const elements = container.querySelectorAll("h1, h2, h3");

    const headingData: Heading[] = Array.from(elements).map((element) => {
      const text = element.textContent || "";
      const id = element.id || generateId(text);

      // define o id se ainda não existir
      if (!element.id) element.id = id;

      return {
        id,
        text,
        level: Number.parseInt(element.tagName.charAt(1)),
      };
    });

    setHeadings(headingData);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: "-100px 0px -80% 0px" },
    );

    elements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div className="mx-auto max-w-7xl space-y-4 px-6 pt-10 pb-20 md:px-8 xl:px-0">
        <h1 className="text-lg font-bold md:text-2xl lg:text-4xl">
          {post.title}
        </h1>
        <PostMetadata
          date={new Date().toLocaleDateString("pt-BR", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
          readTime={`${post.readingTime} min`}
          author={post.author.name}
          avatar={post.author.avatar}
        />

        <div
          className="relative h-96 w-full rounded-lg bg-cover bg-no-repeat lg:bg-fixed"
          style={{ backgroundImage: `url(${post.image})` }}
        ></div>

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_350px] lg:gap-20">
          <article className="mx-auto w-full max-w-7xl">
            <BlogContent
              content={post.content}
              onHeadingsReady={handleHeadingsReady}
            />
          </article>
          <aside>
            <div className="sticky top-28 space-y-6">
              <TableOfContents
                headings={headings}
                activeId={activeId}
                setActiveId={setActiveId}
              />
              <PostTags tags={post.tags} />
              <PostShare title={post.title} />
            </div>
          </aside>
        </div>
        {relatedPosts.length > 0 && (
          <div className="mt-16 border-t border-gray-200 pt-12">
            <RelatedPosts posts={relatedPosts} />
          </div>
        )}
      </div>
    </>
  );
}
